import { useState } from "react";
import { Headphones, Music2, Zap, Users, User, Bell, Search, Plus, Send } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, MaskLine } from "../components/Reveal";

const FAQ_DATA = [
  {
    category: "Getting Started",
    icon: Zap,
    items: [
      { q: "Do I need an account to use Tap In?", a: "Yes. Tap In requires an account. Sign in with your phone number and the verification code we text you. There is no password. The first time, you also choose a username and add your email." },
      { q: "How do I set up or edit my profile?", a: "During onboarding you pick a username, display name, and bio. To change anything later, go to Settings, then Edit Profile, where you can update your photo, bio, location, favorite genres, favorite artists, and social links (Instagram, TikTok, X)." },
      { q: "Why do I need Apple Music?", a: "Tap In uses Apple Music for song playback and search. You need an active Apple Music subscription to play full tracks." },
      { q: "Is Tap In free?", a: "Yes. Tap In is free to use. New accounts start with a 30-day free trial of Tap In Premium, which unlocks unlimited playlists and songs. After the trial, you can keep up to 3 playlists for free, or subscribe to Premium for unlimited. Premium is $5.99/mo or $59.99/yr." },
      { q: "How do I search?", a: "Tap the search bar on Home or open the Search tab to discover music and people. Tap a song to play it, or tap the plus (+) to add it to a playlist, session, or queue it to play next. Tap a person to visit their profile." },
      { q: "Can I search by voice?", a: "Tap the microphone in any search bar and speak. We turn your voice into a search and drop the words into the box. The first time, allow microphone access when asked." },
      { q: "What is my Anthem?", a: "Your Anthem is a song pinned to your profile. Tap \u201cWhat are you listening to today?\u201d on Home to pick one, and it shows on your profile so friends can see what you are listening to right now." },
    ],
  },
  {
    category: "Sessions",
    icon: Headphones,
    items: [
      { q: "How do I host a session?", a: "Tap the center fingerprint button in the bottom nav to open Create, switch to Session mode, then tap Host Now. Give your session a name and choose whether it is public or private. Guests can join with a link or code." },
      { q: "How do I join a session?", a: "Tap any LIVE session in your home feed, open a session link, or scan the session QR code to join. A host can also add you directly as a guest. You will join the live playback instantly, synced with everyone else." },
      { q: "How do I add songs to a session?", a: "In a live session, tap the fingerprint button on the session screen, choose a genre, then pick the song you want to add it to the queue. Guests can add songs when the host enables it." },
      { q: "Can guests control playback?", a: "By default, only the host controls playback. The host can grant access that lets guests add music to the live session." },
      { q: "What are Fire reactions?", a: "Fire is Tap In's version of a like. Tap the fire icon on a song, session, or playlist to react, and the fire count shows how many people have reacted." },
      { q: "Can I see lyrics while listening?", a: "Yes. Tap \u201cLyrics\u201d on the Now Playing screen or in a session to see time-synced lyrics that scroll with the song. Lyrics are not available for every track." },
    ],
  },
  {
    category: "Playlists",
    icon: Music2,
    items: [
      { q: "How do I create a playlist?", a: "Tap the Create tab (center fingerprint), then switch to Playlist mode. Upload a square cover, name your playlist, and choose your permissions. A cover image is required." },
      { q: "What is the difference between a session and a playlist?", a: "Sessions are live: music plays in real time, synced across everyone. Playlists are collections of songs you build and can share or make collaborative." },
      { q: "How do I add or remove songs from a playlist?", a: "To add songs, open the playlist and tap \u201cTap In a Song\u201d to search Apple Music, then tap a song to add it. To remove a song, swipe it left and confirm, or open the song's menu. Changes save automatically." },
      { q: "How do I make a playlist collaborative?", a: "Open the playlist and tap Manage, then turn on guest contributions so others can add music or media. Tap Share to invite people, or let them scan the playlist QR code or open the share link to join and add to it." },
      { q: "How do I react to a playlist?", a: "Tap the fire button on any playlist to react. Reactions help playlists trend and get discovered by more people." },
      { q: "What is a Files playlist?", a: "When creating a playlist you can turn on Files playlist to build a collection of audio files instead of Apple Music tracks. You can also allow guests to add media so friends can contribute their own files." },
      { q: "How do shuffle and repeat work?", a: "On the Now Playing screen, tap shuffle to randomize the order, or repeat to loop. Repeat cycles through off, repeat all, then repeat one (the \u201c1\u201d badge means a single track is looping). Both are off by default, so playback stays in order." },
      { q: "How do I open or close the full player?", a: "Tap the mini player bar at the bottom to expand the full Now Playing screen. Swipe down to dismiss it back to the mini bar. Your music keeps playing." },
      { q: "What does Play Next do?", a: "From a song's plus menu, Play Next queues that song to play right after the current one. Your queue keeps auto advancing to the next song on its own and only stops when you press pause." },
    ],
  },
  {
    category: "Social",
    icon: Users,
    items: [
      { q: "How do I follow someone?", a: "Open their profile (from search or a shared playlist) and tap Follow. You will see their public activity in your feed." },
      { q: "How do DMs work?", a: "DMs are music only. You can share songs, playlists, and sessions with mutual followers. No text messages. Tap Messages, then plus, to start." },
      { q: "How do DM requests work?", a: "If someone you do not follow back messages you, it lands in your Requests tab. Open it to Accept (which moves it to your inbox) or Decline. You are never forced into a conversation." },
      { q: "Can I make a group chat?", a: "Yes. Tap Messages, then the plus, then choose New Group. Select the people you want, add an optional group name and photo, and tap Create. Group chats are still music only, so everyone can share songs and playlists together. From the group menu any member can rename the group, change its photo, add more people, or leave." },
      { q: "How do I block someone?", a: "Open the user's profile, tap the (\u22ee) menu in the top corner, and choose Block. You can also manage blocks in Settings, then Blocked Accounts. Blocked users cannot message or interact with you." },
      { q: "How do I share my profile?", a: "Open your profile and tap your username on the profile banner. Your QR code and shareable link pop up, so anyone can scan the code or open the link to jump straight to your profile." },
      { q: "How do I share another user's profile?", a: "Open their profile, tap the (\u22ee) menu in the top corner, then choose Share Profile. Their QR code and shareable link appear for you to scan or send." },
    ],
  },
  {
    category: "Notifications",
    icon: Bell,
    items: [
      { q: "How do I turn notifications on or off?", a: "Go to Settings, then Notifications. The master Push Notifications toggle turns everything on or off, and when it is off it silences in-app activity, banners, and push. Below it you can fine tune each type on its own: DMs, Playlists, Sessions, and Social." },
    ],
  },
  {
    category: "Account",
    icon: User,
    items: [
      { q: "How do I get the verified artist badge?", a: "You can sign up as an artist during onboarding, or apply anytime in Settings, then Artist Verification, by tapping Apply for Verification. Either way, you match your Apple Music artist profile and add a contact email. Your application goes to Pending review, and the Tap In team adds your blue verified badge once approved, usually within 24 to 48 hours." },
      { q: "How do I connect or disconnect Apple Music?", a: "Go to Settings, then Apple Music, to connect your account, or disconnect it anytime. Apple Music powers full-song playback in both sessions and playlists, so you will not be able to play full tracks until you reconnect." },
      { q: "What is Dropbox used for?", a: "Dropbox is optional. Connect it (or Box) in Settings to import your own audio files into a Files playlist. You can disconnect anytime. It is not required to use Tap In." },
      { q: "What does Tap In Premium include?", a: "Unlimited playlists and unlimited songs per playlist, plus more uploaded media per playlist. $5.99/mo or $59.99/yr, with a 30-day free trial." },
      { q: "How do I report a bug or contact support?", a: "Go to Settings, then Help & Support, where you can Give Feedback, Report a Bug (with steps), or Contact Support. We read every message." },
      { q: "How do I log out?", a: "Go to Settings, then Log Out. Your music stops and you return to the sign-in screen. Your account and content stay safe." },
      { q: "How do I close my account?", a: "Settings, then Close my Account, then confirm. This hard-deletes your account, owned playlists, and notifications. This action cannot be undone." },
    ],
  },
];

const CHIPS = [
  { label: "All", match: null },
  { label: "Getting Started", match: ["Getting Started"] },
  { label: "Sessions", match: ["Sessions"] },
  { label: "Playlists", match: ["Playlists"] },
  { label: "Social", match: ["Social"] },
  { label: "Notifications", match: ["Notifications"] },
  { label: "Account", match: ["Account"] },
];

function FaqItem({ item, index, open, onToggle }) {
  return (
    <div className="glass rounded-2xl overflow-hidden" data-testid={`faq-item-${index}`}>
      <button
        type="button"
        data-testid={`faq-question-${index}`}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="font-semibold text-[0.95rem]">{item.q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="text-grape-soft flex-none">
          <Plus size={18} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-[0.92rem] leading-relaxed text-dusk">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ReportForm() {
  const [form, setForm] = useState({ email: "", subject: "", description: "" });
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/ticket`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setState("done");
      setForm({ email: "", subject: "", description: "" });
    } catch {
      setState("error");
    }
  };

  const field =
    "w-full rounded-2xl border border-grape/25 bg-transparent px-4 py-3 text-[0.95rem] outline-none transition-colors focus:border-grape/60";

  return (
    <form onSubmit={submit} className="mt-8 space-y-4 text-left" data-testid="report-form">
      <div>
        <label htmlFor="report-email" className="block text-sm font-semibold mb-1.5">
          Email
        </label>
        <input
          id="report-email"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="you@example.com"
          className={field}
          data-testid="report-email"
        />
      </div>
      <div>
        <label htmlFor="report-subject" className="block text-sm font-semibold mb-1.5">
          Subject
        </label>
        <input
          id="report-subject"
          type="text"
          required
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          placeholder="Brief description of the issue"
          className={field}
          data-testid="report-subject"
        />
      </div>
      <div>
        <label htmlFor="report-description" className="block text-sm font-semibold mb-1.5">
          Description
        </label>
        <textarea
          id="report-description"
          required
          rows={4}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Please provide as much detail as possible…"
          className={`${field} resize-none`}
          data-testid="report-description"
        />
      </div>
      <button type="submit" disabled={state === "sending"} data-testid="report-submit" className="btn-primary w-full justify-center">
        <Send size={17} /> {state === "sending" ? "Sending…" : "Submit Ticket"}
      </button>
      {state === "done" && (
        <p className="text-sm text-center font-medium text-grape-soft" data-testid="report-success">
          Ticket submitted — we'll get back to you soon.
        </p>
      )}
      {state === "error" && (
        <p className="text-sm text-center text-red-400" data-testid="report-error">
          Something went wrong — please try again.
        </p>
      )}
    </form>
  );
}

export default function Faq() {
  const [cat, setCat] = useState("All");
  const [query, setQuery] = useState("");
  const [openKey, setOpenKey] = useState("0-0");

  const q = query.trim().toLowerCase();
  const activeChip = CHIPS.find((c) => c.label === cat);
  const groups = FAQ_DATA.map((g, gi) => ({
    ...g,
    gi,
    items: g.items
      .map((item, ii) => ({ ...item, ii }))
      .filter((item) => !activeChip?.match || activeChip.match.includes(g.category))
      .filter((item) => !q || `${item.q} ${item.a}`.toLowerCase().includes(q)),
  })).filter((g) => g.items.length > 0);

  return (
    <>
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-10 text-center" data-testid="faq-hero">
        <div className="glow-blob" style={{ width: 560, height: 560, top: "-26%", left: "50%", transform: "translateX(-50%)" }} aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
          <h1 className="hero-title !text-[clamp(2rem,4.6vw,3.4rem)]" data-testid="faq-title">
            <MaskLine delay={0.05}>
              How Can We <span className="text-gradient">Help?</span>
            </MaskLine>
          </h1>
          <p className="body-lg mt-5">Everything you need to know about Tap In</p>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="relative mt-8"
            data-testid="faq-search-wrap"
          >
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-dusk" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for answers…"
              data-testid="faq-search"
              className="w-full rounded-full glass px-11 py-3.5 text-[0.95rem] outline-none border border-grape/25 focus:border-grape/50"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mt-7 flex flex-nowrap overflow-x-auto no-scrollbar justify-start sm:justify-center gap-2 pb-2"
            data-testid="faq-chips"
          >
            {CHIPS.map((c) => {
              const isActive = cat === c.label;
              return (
                <button
                  key={c.label}
                  type="button"
                  onClick={() => setCat(c.label)}
                  data-testid={`faq-chip-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`inline-flex items-center whitespace-nowrap rounded-full px-3 py-1.5 text-[0.8rem] font-medium transition-all duration-300 flex-none ${
                    isActive
                      ? "bg-gradient-to-r from-[#8b4dff] to-[#4d7cff] text-white shadow-glow"
                      : "glass text-chalk/80 hover:text-chalk"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="pb-20" data-testid="faq-list">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          {groups.length === 0 && (
            <p className="text-center body-lg py-14" data-testid="faq-empty">
              No answers match &ldquo;{query}&rdquo; — try another word.
            </p>
          )}
          {groups.map((g) => (
            <div key={g.category} className="mb-12" data-testid={`faq-group-${g.gi}`}>
              <Reveal>
                <h2 className="font-display text-xl sm:text-2xl font-semibold">{g.category}</h2>
              </Reveal>
              <div className="mt-5 space-y-3.5">
                {g.items.map((item) => {
                  const key = `${g.gi}-${item.ii}`;
                  return (
                    <Reveal key={key} delay={item.ii * 0.03}>
                      <FaqItem item={item} index={item.ii} open={openKey === key} onToggle={() => setOpenKey(openKey === key ? "" : key)} />
                    </Reveal>
                  );
                })}
              </div>
            </div>
          ))}

          <Reveal>
            <div className="cta-panel rounded-3xl border border-grape/25 px-6 py-10 text-center mt-14 sm:px-10" data-testid="faq-cta">
              <h2 className="font-display text-2xl">Still have questions?</h2>
              <p className="body-lg mt-2.5 text-[0.95rem]">
                Can't find what you're looking for? Submit a ticket and we'll get back to you.
              </p>
              <ReportForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
